import { Globe, Users, MapPin, Shield, Cog, Building2 } from "lucide-react";
import { WIKI_CATEGORY } from "@/lib/db/schema";

// ─── Category Icons ──────────────────────────────────────

export const CATEGORY_ICONS: Record<string, React.ElementType> = {
    characters: Users,
    locations: MapPin,
    systems: Cog,
    factions: Shield,
    organizations: Building2,
    other: Globe,
};

// ─── Category Colors ─────────────────────────────────────

export const CATEGORY_COLORS: Record<string, { bg: string; border: string; accent: string }> = {
    characters: { bg: "bg-blue-500/30", border: "border-blue-500/40", accent: "text-blue-500" },
    locations: { bg: "bg-emerald-500/30", border: "border-emerald-500/40", accent: "text-emerald-500" },
    systems: { bg: "bg-purple-500/30", border: "border-purple-500/40", accent: "text-purple-500" },
    factions: { bg: "bg-amber-500/30", border: "border-amber-500/40", accent: "text-amber-500" },
    organizations: { bg: "bg-rose-500/30", border: "border-rose-500/40", accent: "text-rose-500" },
    other: { bg: "bg-slate-500/30", border: "border-slate-500/40", accent: "text-slate-500" },
};

// ─── Category Hex Colors (single source of truth) ────────
// Components must reference these (usually via a `--wiki-cat` CSS variable +
// color-mix) rather than hardcoding hexes. Matches the Tailwind classes above.

const CATEGORY_HEX: Record<string, string> = {
    characters: "#3B82F6",
    locations: "#10B981",
    systems: "#A855F7",
    factions: "#F59E0B",
    organizations: "#F43F5E",
    other: "#64748B",
};

export function categoryHex(category: string): string {
    return CATEGORY_HEX[category] ?? CATEGORY_HEX.other;
}

// ─── Category Options (for dropdowns) ────────────────────

export const CATEGORY_OPTIONS = [
    { value: WIKI_CATEGORY.CHARACTERS, label: "Characters" },
    { value: WIKI_CATEGORY.LOCATIONS, label: "Locations" },
    { value: WIKI_CATEGORY.SYSTEMS, label: "Systems" },
    { value: WIKI_CATEGORY.FACTIONS, label: "Factions" },
    { value: WIKI_CATEGORY.ORGANIZATIONS, label: "Organizations" },
    { value: WIKI_CATEGORY.OTHER, label: "Custom..." },
] as const;

// ─── Tiptap Templates (per category type) ────────────────

const CATEGORY_TEMPLATES: Record<string, string[]> = {
    characters: ["Description", "Backstory", "Abilities", "Relationships"],
    locations: ["Description", "Rulers", "Residents", "History"],
    systems: ["Description", "Rules", "Effects", "History"],
    factions: ["Description", "Leaders", "Goals", "Members"],
    organizations: ["Description", "Structure", "Purpose", "Members"],
};

const DEFAULT_TEMPLATE_HEADERS = ["Description", "Details", "Notes"];

export function generateTemplateContent(categoryType: string) {
    const headers = CATEGORY_TEMPLATES[categoryType] ?? DEFAULT_TEMPLATE_HEADERS;
    return {
        type: "doc" as const,
        content: headers.map((header) => ({
            type: "heading" as const,
            attrs: { level: 2 },
            content: [{ type: "text" as const, text: header }],
        })),
    };
}

// ─── Fallback Globe icon ─────────────────────────────────

export { Globe };
